<script lang="ts">
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  iTooltipAlignmentMode,
  iTooltip,
  iBadge,
  iBadgeColours,
} from '@inscribe/signal';
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'ChatPanelHeader',
  components: { iButton, iTooltip, iBadge },
  setup() {
    return {
      iButtonStyles,
      iButtonTypes,
      iButtonSizes,
      iTooltipAlignmentMode,
      iBadgeColours,
    };
  },
  computed: {
    ...mapGetters([
      'chatIsExpanded',
      'currentConversationUuid',
      'getMessagesForConversationUuid',
    ]),
    shouldShowClearChatButton() {
      // Only show this button if there are messages in the conversation
      return (
        this.getMessagesForConversationUuid(this.currentConversationUuid)
          ?.length > 0
      );
    },
  },
  methods: {
    ...mapActions(['toggleChatExpanded', 'closeChat']),
    getKeyboardShortcutForToggleExpandedButton() {
      const isMac = navigator.userAgent.includes('Mac');
      return isMac ? '⌘E' : 'Ctrl+E';
    },
  },
};
</script>

<template>
  <div class="chat-header d-flex align-items-space-between">
    <div class="title i-text-heading-5 d-flex i-gap-4 align-items-center">
      Assistant
      <span id="chat-assistant-secure-badge">
        <i-badge
          :colour="iBadgeColours.NEUTRAL"
          label="Secure"
          :has-leading-icon="true"
          icon="medium-lock"
        ></i-badge>
      </span>
      <i-tooltip
        title="Data is isolated to this workspace and never used for training models."
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
        target="#chat-assistant-secure-badge"
      >
      </i-tooltip>
    </div>
    <div class="tools d-flex align-items-center">
      <i-button
        v-if="shouldShowClearChatButton"
        id="chat-clear-button"
        label="Clear Chat"
        :button-style="iButtonStyles.SOLID_LIGHT"
        :size="iButtonSizes.SMALL"
        :type="iButtonTypes.NEUTRAL"
        class="i-mr-6"
        @button-click="$emit('clear-chat')"
      ></i-button>
      <i-tooltip
        v-if="shouldShowClearChatButton"
        title="Clear Chat"
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
        target="#chat-clear-button"
      >
      </i-tooltip>
      <div class="buttons d-flex i-gap-2">
        <i-button
          id="chat-toggle-expanded-button"
          aria-label="Chat panel: Toggle size"
          data-dd-action-name="chat-panel-toggle-size"
          :left-icon="
            chatIsExpanded ? 'medium-arrows-shrink-3' : 'medium-arrows-expand-3'
          "
          :button-style="iButtonStyles.GHOST"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          @button-click="toggleChatExpanded"
        ></i-button>
        <i-tooltip
          :title="chatIsExpanded ? 'Collapse' : 'Expand'"
          :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
          target="#chat-toggle-expanded-button"
        >
          <template #tools>
            <div class="shortcut i-ml-2 i-text-body-3 i-text-neutral-90">
              {{ getKeyboardShortcutForToggleExpandedButton() }}
            </div>
          </template>
        </i-tooltip>
        <i-button
          id="chat-close-button"
          aria-label="Chat panel: Close"
          data-dd-action-name="chat-panel-close"
          left-icon="medium-delete-1"
          :button-style="iButtonStyles.GHOST"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          @button-click="closeChat"
        ></i-button>
        <i-tooltip
          title="Close"
          :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
          target="#chat-close-button"
        >
          <template #tools>
            <div class="shortcut i-ml-2 i-text-body-3 i-text-neutral-90">
              Esc
            </div>
          </template>
        </i-tooltip>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chat-header {
  .title {
    color: var(--text-neutral-bold);
    flex: 1;
  }
  .shortcut {
    font-family: 'Roboto Mono';
  }
}
</style>
